import React, {
	useMemo,
	type JSXElementConstructor,
	type MutableRefObject,
	type ReactNode,
} from 'react';
import { ButtonMenuItem } from '@atlaskit/navigation-system';
import type { ItemsConfig } from '@atlassian/global-side-navigation/types';
import type {
	CustomItemConfig,
	CustomItemProps,
	NavItemConfig,
} from '@atlassian/navigation-common/types';
import { Nav4Filters } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-filters/src/index.tsx';
import {
	L1_MENU_ID,
	type JiraConfigurableNavigationItem,
	type Nav4DragAndDropMenuItemProps,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { getUserNavigationItemLinks } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-user-navigation-item-links/index.tsx';
import { Nav4Apps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-apps/src/index.tsx';
import { Nav4Assets } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-assets/src/ui/index.tsx';
import { CustomerService } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-customer-service/src/index.tsx';
import { Nav4Dashboards } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-dashboards/src/index.tsx';
import { Nav4Operations } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-operations/src/ui/Nav4Operations.tsx';
import { Nav4Overviews } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-overviews/src/index.tsx';
import { Nav4Plans } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-plans/src/index.tsx';
import { Nav4Projects } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-projects-core/src/index.tsx';
import { Nav4Roadmaps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-roadmaps/src/index.tsx';
import { Teams } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { mergeRefs } from '@atlassian/jira-merge-refs';

import { isTeamsAppEnabled } from '@atlassian/jira-teams-app-config/src/index.tsx';
import { getConfigurableMessageAndIcon } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { isConfigurableMenuId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/is-configurable-menu-id/index.tsx';
import { useIntl } from '@atlassian/jira-intl';

import type { GSNForContentSidebarQuery$data } from '../../ui/__generated__/GSNForContentSidebarQuery.graphql';

type ComponentProps = {
	queryRef: GSNForContentSidebarQuery$data;
	isExpanded: boolean;
};

export function useItemsConfig(
	navItems: JiraConfigurableNavigationItem[],
	queryRef: GSNForContentSidebarQuery$data,
	shouldRenderOverviews: boolean,
): ItemsConfig {
	const { formatMessage } = useIntl();

	return useMemo(() => {
		const componentProps: ComponentProps = { isExpanded: false, queryRef };

		return navItems.reduce<ItemsConfig>((acc, { menuId }) => {
			// We need a configurable menu id and a component to continue
			if (isConfigurableMenuId(menuId)) {
				const { message, Icon } = getConfigurableMessageAndIcon(menuId);
				const label = formatMessage(message);
				// See src/packages/navigation-apps/sidebar-nav4/sidebars/common/core/src/common/utils/use-formatted-message-and-icon/index.tsx
				const elemBefore = <Icon label={label} spacing="spacious" color="currentColor" />;
				const itemConfig = mapMenuIdToNavItemConfig({
					componentProps,
					elemBefore,
					label,
					menuId,
					shouldRenderOverviews,
				});

				if (itemConfig) {
					acc[menuId] = itemConfig;
				}
			}

			return acc;
		}, {});
	}, [formatMessage, navItems, queryRef, shouldRenderOverviews]);
}

interface mapMenuIdToComponentParams {
	componentProps: ComponentProps;
	elemBefore: ReactNode;
	label: string;
	shouldRenderOverviews: boolean;
	menuId: string;
}

function mapMenuIdToNavItemConfig(params: mapMenuIdToComponentParams): NavItemConfig | null {
	const { componentProps, elemBefore, label, menuId, shouldRenderOverviews } = params;

	switch (menuId) {
		case L1_MENU_ID.APPS:
			return buildCustomComponentConfig({
				Component: Nav4Apps,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.ASSETS:
			/* remove this case on
			 * assets_as_an_app_v2 cleanup
			 */
			return buildCustomComponentConfig({
				Component: Nav4Assets,
				componentProps,
				elemBefore,
				label,
				isDraggable: false, // Not draggable
			});
		case L1_MENU_ID.DASHBOARDS:
			return buildCustomComponentConfig({
				Component: Nav4Dashboards,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.FILTERS:
			return buildCustomComponentConfig({
				Component: Nav4Filters,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.GOALS: {
			const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();
			return {
				type: 'link',
				elemBefore,
				label,
				href: USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GOALS],
			};
		}
		case L1_MENU_ID.OPERATIONS:
			return buildCustomComponentConfig({
				Component: Nav4Operations,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.OVERVIEWS:
			return shouldRenderOverviews
				? buildCustomComponentConfig({
						Component: Nav4Overviews,
						componentProps,
						elemBefore,
						label,
						isDraggable: true,
					})
				: null;
		case L1_MENU_ID.PLANS:
			return buildCustomComponentConfig({
				Component: Nav4Plans,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.PROJECTS:
			return buildCustomComponentConfig({
				Component: Nav4Projects,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.ROADMAPS:
			return buildCustomComponentConfig({
				Component: Nav4Roadmaps,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.TEAMS:
			return !isTeamsAppEnabled()
				? buildCustomComponentConfig({
						Component: Teams,
						componentProps,
						elemBefore,
						label,
						isDraggable: true,
					})
				: null;
		case L1_MENU_ID.CUSTOMER_SERVICE:
			return fg('jcs_master_flag')
				? buildCustomComponentConfig({
						Component: CustomerService,
						componentProps,
						elemBefore,
						label,
						isDraggable: true,
					})
				: null;
		default:
			return null;
	}
}

interface CustomComponentConfigParams {
	Component: JSXElementConstructor<Nav4DragAndDropMenuItemProps & ComponentProps>;
	componentProps: ComponentProps;
	elemBefore: ReactNode;
	isDraggable: boolean;
	label: string;
}

function buildCustomComponentConfig(params: CustomComponentConfigParams): CustomItemConfig {
	const { Component, componentProps, elemBefore, isDraggable, label } = params;

	return {
		type: 'custom',
		elemBefore,
		isDraggable: isDraggable && fg('navx-global-side-nav'),
		isMenuListItem: true, // All of these components are already wrapped in MenuListItem
		label,
		Component: (customItemProps: CustomItemProps) => {
			const { isInMoreMenu, visualContentRef } = customItemProps;

			// When the item isInMoreMenu, we cannot render directly as this breaks the Peek logic.
			if (isInMoreMenu) {
				// TODO: We should handle this internally in GSN to avoid duplicating this logic
				return <MoreMenuComponent elemBefore={elemBefore} label={label} {...customItemProps} />;
			}
			const fixVisualContentRef =
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				visualContentRef as MutableRefObject<HTMLDivElement | null>;

			return (
				<Component
					{...componentProps}
					{...customItemProps}
					visualContentRef={fixVisualContentRef}
				/>
			);
		},
	};
}

interface MoreMenuCompProps extends CustomItemProps {
	elemBefore: ReactNode;
	label: string;
}

function MoreMenuComponent(props: MoreMenuCompProps) {
	const { draggableRef, elemBefore, label, onClick, onPeek, visualContentRef } = props;
	// This is the same transformation happening inside GSN
	const handleClick = () => {
		onPeek?.();
		onClick?.();
	};

	// Need to coerce this type for ButtonMenuItem
	const fixVisualContentRef =
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		visualContentRef as MutableRefObject<HTMLButtonElement | null>;

	// todo: We should handle this internally in GSN to avoid duplicating this logic
	return (
		<ButtonMenuItem
			onClick={handleClick}
			elemBefore={elemBefore}
			ref={draggableRef ? mergeRefs(draggableRef, fixVisualContentRef) : fixVisualContentRef}
		>
			{label}
		</ButtonMenuItem>
	);
}
